/**
 * 一覧の型の絞りと合計（#302、2026-11-04 経理担当の指摘 2本目、docs/94 B-3 の追記・Figma「一覧の見直し（11/4）」①）。
 * 見積書・請求書・{deliveryNote}・入金の4つの一覧が同じ決まりで使う：
 *   ・期間の絞り（すべて／今月／先月／今期／期間指定。既定は すべて）＝帳票の日付（見積日・請求日・納品日・入金日）で絞る
 *   ・{customer}の絞り（取引先検索のドロアーで選ぶ）
 *   ・表の上の右端に絞った結果の「n件・税抜・税込」（請求書は残額の合計も・入金は入金額と未消込額）
 *   ・合計はタブ・期間・{customer}・絞り込み（検索）を全部かけた結果。タブの件数も期間・{customer}・検索をかけた数
 * {customer}の名前は略称を優先（空なら正式名称）＝listNameOf。紙・PDF・メール・取引先の詳細は正式名称のまま
 */
import { z } from "zod";

export const ListPeriod = z.enum(["all", "thisMonth", "lastMonth", "thisTerm", "range"]);
export type ListPeriod = z.infer<typeof ListPeriod>;

export const LIST_PERIOD_LABEL: Record<ListPeriod, string> = {
  all: "すべて",
  thisMonth: "今月",
  lastMonth: "先月",
  thisTerm: "今期",
  range: "期間指定",
};

/** 一覧の言葉（4つの一覧の列見出し・絞り・合計。Figma ③「案」） */
export const LIST_MSG = {
  period: "期間",
  periodFrom: "開始日",
  periodTo: "終了日",
  customerAll: "すべて",
  customerClear: "絞り解除",
  count: (n: number) => `${n.toLocaleString("ja-JP")}件`,
  subtotal: "税抜",
  total: "税込",
  balance: "残額",
  amount: "入金額",
  unallocated: "未消込額",
  /** 列見出し（{customer}・{subject}・{project}は会社の言葉。画面は t() を通す） */
  colQuoteDate: "見積日",
  colInvoiceDate: "請求日",
  colNoteDate: "納品日",
  colPayDate: "入金日",
  colNo: "番号",
  colState: "状態",
  colOrder: "受注",
  colBill: "請求",
  colDue: "支払期限",
  colTotal: "金額（税込）",
  colSubtotal: "金額（税抜）",
  colBalance: "残額",
  colPick: "選択",
  colInvoice: "請求書",
  colAmount: "入金額",
  colMethod: "方法",
  colUnallocated: "未消込額",
  /** 番号の列の下書き（全部の一覧で同じ。前は{deliveryNote}だけ「—」） */
  unissued: "未発行",
  /** 2行目：{project}名が{subject}と違うとき（#302 A3） */
  groupLine: (project: string, name: string) => `${project}：${name} ›`,
  /** 2行目：分割（見積書は請求した回／回数・請求書はその回／回数） */
  split: (n: number, m: number) => `分割 ${n}／${m}回`,
  closingMonth: (m: number) => `締め ${m}月分`,
  recurringMonth: (m: number) => `定期 ${m}月分`,
  /** 請求書作成（見積から／{deliveryNote}から／締め請求の3択） */
  invoiceCreate: "請求書作成",
  invoiceFromQuote: "見積から",
  invoiceFromNotes: "{deliveryNote}から",
  invoiceClosing: "締め請求",
  searchDoc: "番号・{customer}・{subject}で絞り込み",
  searchPayment: "{customer}・請求書番号で絞り込み",
  /** 乗せたときの説明（受注・失注を決めた日は2行目に出さない＝#302 A3） */
  decidedOn: (state: string, md: string) => `${md}に${state}`,
  /** 送付の列と絞り（#303 B3、経理担当の指摘 20） */
  colSend: "送付",
  sendFilter: "送付",
  sendAll: "すべて",
  /** 行の ⋯（#304）：送付を手で切り替える＝送付の記録に「手動」の行が1つ残る */
  rowMenu: "操作",
  markSent: "送付済にする",
  markUnsent: "未送付に戻す",
  markedSent: "送付済にしました。",
  markedUnsent: "未送付に戻しました。",
} as const;

/**
 * 送付の状態（#303 B3）：送付の記録（document_deliveries）から。未送付／送付済（印刷・PDF 保存・メールの送信・FAX）／取得済（メールの閲覧ページを開いた）。
 * 送信失敗・届いていません・送信待ちのメールは送付に数えない。請求書の一覧の列と絞り（見積書の一覧は同じ部品で後から）
 */
export const SendState = z.enum(["none", "sent", "opened"]);
export type SendState = z.infer<typeof SendState>;
export const SEND_STATE_LABEL: Record<SendState, string> = { none: "未送付", sent: "送付済", opened: "取得済" };
/** 送付の方法（乗せたときの説明）：印刷・PDF 保存・メール・FAX・手動（#304＝一覧の ⋯ の「送付済にする」）・その他（ネット・リンク） */
export const SendMethod = z.enum(["print", "download", "email", "fax", "manual", "other"]);
export type SendMethod = z.infer<typeof SendMethod>;
const SEND_METHOD_LABEL: Record<SendMethod, string> = { print: "印刷", download: "PDF 保存", email: "メール", fax: "FAX", manual: "手動", other: "送付" };
/**
 * 一覧の行の送付（API が送付の記録から作る）。at＝最後の送付の日時・openedAt＝閲覧ページを最初に開いた日時。
 * resetAt＝「未送付に戻す」（手動）の日時（#304）＝それより前の送付は数えない。未送付のまま乗せると「未送付に戻した 日付（手動）」
 */
export const SendSummary = z.object({ state: SendState, at: z.string().nullable(), method: SendMethod.nullable(), openedAt: z.string().nullable(), resetAt: z.string().nullable().optional() });
export type SendSummary = z.infer<typeof SendSummary>;
/** 絞り（URL の ?send=）。省けば すべて */
export const ListSendFilter = z.enum(["all", "none", "sent", "opened"]);
export type ListSendFilter = z.infer<typeof ListSendFilter>;
/** 乗せたときの日付（日本時間の YYYY/MM/DD）：送付済「メール 2026/11/04」・取得済「取得 2026/11/05（メール 2026/11/04）」・未送付は空 */
export function sendTitleOf(s: SendSummary | null | undefined): string {
  const ymd = (iso: string) => {
    const d = new Date(new Date(iso).getTime() + 9 * 3600_000);
    return `${d.getUTCFullYear()}/${String(d.getUTCMonth() + 1).padStart(2, "0")}/${String(d.getUTCDate()).padStart(2, "0")}`;
  };
  if (s && s.state === "none" && s.resetAt) return `未送付に戻した ${ymd(s.resetAt)}（手動）`;
  if (!s || s.state === "none" || !s.at) return "";
  const sent = `${SEND_METHOD_LABEL[s.method ?? "other"]} ${ymd(s.at)}`;
  return s.state === "opened" && s.openedAt ? `取得 ${ymd(s.openedAt)}（${sent}）` : sent;
}
/** 送付を手で切り替える（POST /invoices/:id/send-state、#304）。sent＝送付済にする／unsent＝未送付に戻す。今と同じ状態なら記録を足さない */
export const SendStateUpdate = z.object({ state: z.enum(["sent", "unsent"]) });
export type SendStateUpdate = z.infer<typeof SendStateUpdate>;

/** 絞りに合うか（すべては全部・未送付は none・送付済は sent と opened・取得済は opened） */
export const sendFilterHit = (f: ListSendFilter, s: SendSummary | null | undefined): boolean => {
  const st = s?.state ?? "none";
  return f === "all" ? true : f === "none" ? st === "none" : f === "sent" ? st !== "none" : st === "opened";
};

/** 一覧に出す{customer}の名前：略称（空白だけは空）を優先・無ければ正式名称（#302 A2。displayNameOf のように「株式会社」を外さない） */
export const listNameOf = (p: { name: string; shortName?: string | null }): string => (p.shortName ?? "").trim() || p.name;

/** 'YYYY-MM-DD' の月の初日・末日 */
const firstOf = (y: number, m: number): string => `${y}-${String(m).padStart(2, "0")}-01`;
const lastOf = (y: number, m: number): string => {
  const d = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
};

/**
 * 期間の範囲（両端を含む・YYYY-MM-DD）。all は両方 null。range は渡した from・to（片方だけでもよい。逆なら入れ替える）。
 * 今期＝会計年度（fiscalStartMonth＝始まりの月。既定 4）の今の期の初日から末日まで
 */
export function listPeriodRange(kind: ListPeriod, today: string, fiscalStartMonth = 4, from?: string | null, to?: string | null): { from: string | null; to: string | null } {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  switch (kind) {
    case "all":
      return { from: null, to: null };
    case "thisMonth":
      return { from: firstOf(y, m), to: lastOf(y, m) };
    case "lastMonth": {
      const py = m === 1 ? y - 1 : y;
      const pm = m === 1 ? 12 : m - 1;
      return { from: firstOf(py, pm), to: lastOf(py, pm) };
    }
    case "thisTerm": {
      const s = Math.min(12, Math.max(1, Math.trunc(fiscalStartMonth) || 4));
      const sy = m >= s ? y : y - 1;
      const ey = s === 1 ? sy : sy + 1;
      const em = s === 1 ? 12 : s - 1;
      return { from: firstOf(sy, s), to: lastOf(ey, em) };
    }
    case "range": {
      const f = from && /^\d{4}-\d{2}-\d{2}$/.test(from) ? from : null;
      const t = to && /^\d{4}-\d{2}-\d{2}$/.test(to) ? to : null;
      return f && t && f > t ? { from: t, to: f } : { from: f, to: t };
    }
  }
}

/** 日付（YYYY-MM-DD。日時の頭でもよい）が範囲に入るか */
export const inListPeriod = (date: string | null | undefined, r: { from: string | null; to: string | null }): boolean => {
  if (!r.from && !r.to) return true;
  if (!date) return false;
  const d = date.slice(0, 10);
  return (!r.from || d >= r.from) && (!r.to || d <= r.to);
};

/** 一覧の絞り込み（検索）の文字：大文字小文字・全角半角を同じに見る */
export const listQueryNorm = (s: string): string => s.normalize("NFKC").toLowerCase().replace(/\s+/g, " ").trim();
/** どれかの文字に q を含むか（空なら true） */
export const listQueryHit = (q: string, ...texts: Array<string | null | undefined>): boolean => {
  const n = listQueryNorm(q);
  if (!n) return true;
  return texts.some((t) => t != null && listQueryNorm(t).includes(n));
};

/** 一覧の日付（2026/11/02）。Figma ① の形 */
export const ymdSlash = (iso: string | null | undefined): string => (iso ? `${iso.slice(0, 4)}/${iso.slice(5, 7)}/${iso.slice(8, 10)}` : "");
